import { useCallback, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AnalyticsScreen } from 'shared/src/context';
import type { ILogging } from 'shared/src/api/types/ILogging';
import { useInstanceId } from 'shared/src/hooks';
import { getSearchResultInfiniteQueryData } from '../queries/useSearchResultQuery';

type Props = {
  screenName: string;
  query: string;
  nextToken?: string;
  children: ReactNode;
};

/**
 * SearchResult 화면 전용 AnalyticsScreen wrapper.
 * - useSearchResultQuery 캐시의 마지막 페이지 logging을 getDefaults로 주입
 * - useLogAnalyticsEvent wrapper를 통해 호출 사이트의 analytics에 자동 머지됨
 * - ScreensAnalyticsProvider와 동일 철학 (queryKey만 검색용)
 * - 캐시 미스 시 undefined fallback → SCREEN만 머지되어 회귀 0 보장
 */
export const SearchAnalyticsProvider = ({
  screenName,
  query,
  nextToken,
  children,
}: Props) => {
  const queryClient = useQueryClient();
  const instanceId = useInstanceId();

  const getDefaults = useCallback((): ILogging | undefined => {
    const data = getSearchResultInfiniteQueryData(queryClient, {
      screenName,
      query,
      nextToken,
      instanceId,
    });
    return data?.pages[data.pages.length - 1]?.logging;
  }, [queryClient, screenName, query, nextToken, instanceId]);

  return (
    <AnalyticsScreen screen={screenName} getDefaults={getDefaults}>
      {children}
    </AnalyticsScreen>
  );
};
